
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisUiService } from '../base/his-ui.service';

/**
 * communication with the HISinOne server
 */
@Injectable({providedIn: 'root'})
export class HisBackendService {
    private http = inject(HttpClient);
    private notificationService = inject(HisNotificationService);
    private hisUiService = inject(HisUiService);


    private prefix = '../../';
    private bearerToken?: string;
    private testcase?: string;

    /**
     * displays an error message as notification and logs the details of the error
     *
     * @param requestOptions specified when making the request
     * @param err internal error message
     */
    private handleError( requestOptions: HisBackendRequestOptions, err: any ) {
        let body = err.error;
        if ( !body ) {
            body = {};
        }

        if ( err.status === 401 || err.status === 403 ) {
            // Unauthorized

            if ( !requestOptions || requestOptions.showPermissionError !== false ) {
                let errorMessageTitle = this.hisUiService.translate( 'common.errorpage.title' ) || 'Unauthorized';
                let errorMessageText = this.hisUiService.translate( 'common.service.accessDenied' ) || 'No permisions';
                this.notificationService.clear();
                this.notificationService.error( errorMessageText, errorMessageTitle );
            }

        } else if ( err.status === 422 ) {
            // Validation error

            if ( !requestOptions || requestOptions.showValidationError !== false ) {
                let errorMessageTitle = this.hisUiService.translate( 'view.messages.error' ) || 'Validation Error';
                let errorMessageText = null;
                let errorException = body as ExceptionResponse;

                if ( errorException.messages && errorException.messages.length > 0 ) {
                    errorMessageText = [];
                    for ( let entry of errorException.messages ) {
                        this.notificationService.notifyFieldMessage( entry );
                        if ( entry.message ) {
                            errorMessageText.push( entry.message );
                        } else if ( entry.messageKey && entry.messageParameters ) {
                            let messageParameters: ( string | number | Date )[] = this.adaptMessageParameters( entry.messageParameters );
                            errorMessageText.push( this.hisUiService.translate( entry.messageKey, ...messageParameters ) || 'Not found' );
                        } else if ( entry.messageKey ) {
                            errorMessageText.push( this.hisUiService.translate( entry.messageKey ) || 'Not found' );
                        } else {
                            errorMessageText.push( 'Not found' );
                        }
                    }
                } else {
                    if ( errorException.description ) {
                        errorMessageText = errorException.description;
                    } else if ( errorException.descriptionKey && errorException.descriptionParameters ) {
                        let descriptionParameters: ( string | number | Date )[] = this.adaptMessageParameters( errorException.descriptionParameters );
                        errorMessageText = this.hisUiService.translate( errorException.descriptionKey, ...descriptionParameters ) || 'Not found';
                    } else if ( errorException.descriptionKey ) {
                        errorMessageText = this.hisUiService.translate( errorException.descriptionKey ) || 'Not found';
                    } else {
                        errorMessageText = errorMessageTitle;
                    }
                }
                this.notificationService.clear();
                this.notificationService.error( errorMessageText, errorMessageTitle );
            }

        } else if ( err.status === 404 ) {
            if ( !requestOptions || requestOptions.showNotFoundError !== false ) {
                let errorMessageTitle = this.hisUiService.translate( 'common.errorpage.title' ) || 'Not found';
                let errorMessageText = this.hisUiService.translate( 'common.errorpage.notfound' ) || 'Not found';
                this.notificationService.clear();
                this.notificationService.error( errorMessageText, errorMessageTitle );
            }
        } else {
            // Any other kind of error: Display general message. Do not include err.statusText
            // (e.g. "Internal Server Error") because it may be "OK", for example on 422.
            if ( !requestOptions || requestOptions.showUnexpectedError !== false ) {
                let errorMessageTitle = this.hisUiService.translate( 'common.errorpage.title' ) || 'Internal error';
                let errorMessageText = [this.hisUiService.translate( 'common.errorpage.text', '' ) || 'An internal error occured'];
                if ( body.description ) {
                    errorMessageText.push( 'Detail: ' + body.description );
                } else if ( body.descriptionKey && body.descriptionParameters ) {
                    let descriptionParameters: ( string | number | Date )[] = this.adaptMessageParameters( body.descriptionParameters );
                    errorMessageText.push( this.hisUiService.translate( body.descriptionKey, ...descriptionParameters ) || 'Not found' );
                } else if ( body.descriptionKey ) {
                    errorMessageText.push( this.hisUiService.translate( body.descriptionKey ) || 'Not found' );
                }
                console.error( 'backend returned error:', err );
                this.notificationService.clear();
                this.notificationService.error( errorMessageText, errorMessageTitle );
            }
        }
    }

    private adaptMessageParameters( paramsObject: Record<number | string, string | number | Date> ): ( string | number | Date )[] {
        let result: ( string | number | Date )[] = [];
        let parameterKeys: number[] = Object.keys( paramsObject ).map( Number );
        let parameterNumber = parameterKeys.reduce( ( a, b ) => Math.max( a, b ), -Infinity );
        if ( isFinite( parameterNumber ) ) {
            parameterNumber += 1;
            let parIdx: number = 0;
            while ( parIdx < parameterNumber ) {
                if ( parIdx in parameterKeys ) {
                    result[parIdx] = paramsObject[parIdx];
                } else {
                    result[parIdx] = '';
                }
                parIdx += 1;
            }
        }
        return result;
    }

    private createOptions( requestOptions?: HisBackendRequestOptions ): any {
        let header: any = {};
        let bearerToken = requestOptions?.bearerToken ?? this.bearerToken;
        if ( bearerToken ) {
            header = {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Authorization: 'Bearer ' + bearerToken
            };
        }
        if ( this.testcase ) {
            header['X-HIS-Testcase'] = this.testcase;
        }
        if ( requestOptions && requestOptions.blockUI === false ) {
            header['X-HIS-Block-Ui'] = 'false';
        }

        let options = {
            headers: header
        };
        return options;
    }

    /**
     * invokes a HISinOne REST service to read data
     */
    public getData( url: string, requestOptions?: HisBackendRequestOptions ): Observable<any> {
        this.updateLastRefresh();
        let options = this.createOptions( requestOptions );
        console.log( 'getData #options', options );
        let result = this.http.get( this.prefix + url, options );
        console.log( 'getData #this.prefix', this.prefix, '#url', url );
        //console.log('getData #result', result);
        return result.pipe(
            tap( {
                error: ( err ) => this.handleError( requestOptions, err )
            } ) );
    }

    public postData( url: string, data: any, requestOptions?: HisBackendRequestOptions ): Observable<any> {
        this.updateLastRefresh();
        let options = this.createOptions( requestOptions );
        console.log( 'postData #this.prefix', this.prefix, '#url', url, '#data', data, '#options', options );
        let result = this.http.post( this.prefix + url, data, options );
        return result.pipe(
            tap( {
                error: ( err ) => this.handleError( requestOptions, err )
            } ) );
    }

    public putData( url: string, data: any, requestOptions?: HisBackendRequestOptions ): Observable<any> {
        this.updateLastRefresh();
        let options = this.createOptions( requestOptions );
        let result = this.http.put( this.prefix + url, data, options );
        return result.pipe(
            tap( {
                error: ( err ) => this.handleError( requestOptions, err )
            } ) );
    }

    public saveData( url: string, data: any, requestOptions?: HisBackendRequestOptions ) {
        if ( data.id ) {
            return this.putData( url + '/' + data.id, data, requestOptions );
        } else {
            return this.postData( url, data, requestOptions );
        }
    }

    public delete( url: string, requestOptions?: HisBackendRequestOptions ): Observable<any> {
        this.updateLastRefresh();
        let options = this.createOptions( requestOptions );
        let result = this.http.delete( this.prefix + url, options );
        return result.pipe(
            tap( {
                error: ( err ) => this.handleError( requestOptions, err )
            } ) );
    }

    /**
     * update the lastRefresh cookie which is used for the countdown and client side logout.
     */
    public updateLastRefresh(): void {
        let lastRefresh = new Date().getTime();

        // set path to the contextPath of the web application
        let path = document.location.pathname;
        let endPosition = path.indexOf( '/', 1 );
        document.cookie = 'lastRefresh=' + lastRefresh + ';path=' + path.substring( 0, endPosition ) + ';SameSite=Lax;Secure';
    }

    public initTestMode( prefix: string, testcase?: string, bearerToken?: string ) {
        this.prefix = prefix;
        if ( testcase ) {
            if ( testcase.indexOf( ' ' ) < 0 ) {
                console.log( 'Deprecated initTestMode invokation' );
                this.bearerToken = testcase;
                return;
            }
        }
        this.testcase = testcase;
        this.bearerToken = bearerToken;
    }
}

export class HisBackendRequestOptions {
    public showPermissionError?: boolean = true;
    public showValidationError?: boolean = true;
    public showNotFoundError?: boolean = true;
    public showUnexpectedError?: boolean = true;
    public blockUI?: boolean = true;
    public bearerToken?: string = undefined;
}

export class ErrorMessage {
    field: string;
    message?: string;
    messageKey?: string;
    messageParameters?: Record<number | string, string | number | Date>;
}

export class ExceptionResponse {
    source: string;
    description?: string;
    descriptionKey?: string;
    descriptionParameters?: Record<number | string, string | number | Date>;
    messages: ErrorMessage[];
}
